import { useState, useEffect, useCallback } from 'react';
import { trackingService, TrackingEvent, StudentProgress, AnalyticsData } from '../services/trackingService';
import { DataAndReporting } from '../types';

interface UseTrackingOptions {
  lessonId: string;
  studentId: string;
  dataAndReporting?: DataAndReporting;
  sessionId?: string;
}

interface UseTrackingReturn {
  // Tracking functions
  trackEvent: (eventType: string, data?: Record<string, any>) => Promise<TrackingEvent | null>;
  trackStepComplete: (stepId: string, timeSpent: number, completed?: boolean) => Promise<void>;
  trackAssessmentComplete: (assessmentId: string, score: number, timeSpent: number) => Promise<void>;
  trackResourceInteraction: (resourceId: string, actionType: string, duration?: number, status?: boolean, score?: number) => Promise<void>;
  trackQuizResponse: (questionId: string, answer: any, correct: boolean, timeSpent: number) => Promise<void>;
  trackEngagementCheck: (checkType: string, response: any) => Promise<void>;
  trackExitTicket: (response: string, understandingLevel: number, timeSpent: number) => Promise<void>;

  // Data access
  getStudentProgress: () => Promise<StudentProgress | null>;
  getAnalytics: () => Promise<AnalyticsData | null>;
  getLessonEvents: () => Promise<TrackingEvent[]>;
  getLessonSummary: () => Promise<{
    totalStudents: number;
    averageTime: number;
    averageScore: number;
    completionRate: number;
    engagementRate: number;
  }>;

  // Export functions
  exportData: (format?: 'csv' | 'json' | 'pdf') => Promise<string>;

  // State
  isTrackingEnabled: boolean;
  currentSessionId: string;
}

export const useTracking = ({
  lessonId,
  studentId,
  dataAndReporting,
  sessionId
}: UseTrackingOptions): UseTrackingReturn => {
  const [currentSessionId] = useState(sessionId || `session_${Date.now()}`);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);

  // Check if tracking is enabled based on configuration
  useEffect(() => {
    if (dataAndReporting) {
      setIsTrackingEnabled(dataAndReporting.analytics_enabled || false);
    }
  }, [dataAndReporting]);

  // Generic tracking function
  const trackEvent = useCallback(async (
    eventType: string,
    data: Record<string, any> = {}
  ): Promise<TrackingEvent | null> => {
    if (!isTrackingEnabled) return null;

    // Check if this event should be tracked based on configuration
    if (dataAndReporting && !trackingService.shouldTrackEvent(eventType, dataAndReporting)) {
      console.log('not tracking event', eventType, data, dataAndReporting);
      return null;
    }

    const event = dataAndReporting?.events.find(e => e.when === eventType);
    if (event) {
      if (event.capture.includes('student_id')) {
        data.studentId = studentId;
      }
    }

    // Add student and lesson context to data
    const eventData = {
      ...data,
      lessonId,
      sessionId: currentSessionId
    };
    console.log('eventData', eventData);

    try {
      return await trackingService.trackEvent(
        lessonId,
        studentId,
        eventType,
        eventData,
        currentSessionId
      );
    } catch (error) {
      console.error('Error tracking event:', error);
      return null;
    }
  }, [lessonId, studentId, currentSessionId, isTrackingEnabled, dataAndReporting]);

  // Specific tracking functions
  const trackStepComplete = useCallback(async (
    stepId: string,
    timeSpent: number,
    completed: boolean = true
  ): Promise<void> => {
    await trackEvent('step_complete', {
      stepId,
      timeSpentSeconds: timeSpent,
      completed
    });
  }, [trackEvent]);

  const trackAssessmentComplete = useCallback(async (
    assessmentId: string,
    score: number,
    timeSpent: number
  ): Promise<void> => {
    await trackEvent('assessment_complete', {
      assessmentId,
      score,
      timeSpentSeconds: timeSpent,
      itemsCorrect: score // Assuming score represents correct items
    });
  }, [trackEvent]);

  const trackResourceInteraction = useCallback(async (
    resourceId: string,
    actionType: string,
    duration: number = 0,
    status: boolean = false,
    score: number = 0
  ): Promise<void> => {
    await trackEvent('resource_interaction', {
      resourceId,
      actionType,
      durationSeconds: duration,
      status,
      score
    });
  }, [trackEvent]);

  const trackQuizResponse = useCallback(async (
    questionId: string,
    answer: any,
    correct: boolean,
    timeSpent: number
  ): Promise<void> => {
    await trackEvent('quiz_response', {
      questionId,
      answer,
      correct,
      timeSpentSeconds: timeSpent
    });
  }, [trackEvent]);

  const trackEngagementCheck = useCallback(async (
    checkType: string,
    response: any
  ): Promise<void> => {
    await trackEvent('engagement_check', {
      checkType,
      response,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackExitTicket = useCallback(async (
    response: string,
    understandingLevel: number,
    timeSpent: number
  ): Promise<void> => {
    await trackEvent('exit_ticket_submit', {
      response,
      understandingLevel,
      timeSpentSeconds: timeSpent
    });
  }, [trackEvent]);

  // Data access functions
  const getStudentProgress = useCallback(async (): Promise<StudentProgress | null> => {
    return await trackingService.getStudentProgress(lessonId, studentId);
  }, [lessonId, studentId]);

  const getAnalytics = useCallback(async (): Promise<AnalyticsData | null> => {
    return await trackingService.getAnalytics(lessonId, studentId);
  }, [lessonId, studentId]);

  const getLessonEvents = useCallback(async (): Promise<TrackingEvent[]> => {
    return await trackingService.getLessonEvents(lessonId);
  }, [lessonId]);

  const getLessonSummary = useCallback(async () => {
    return await trackingService.getLessonSummary(lessonId);
  }, [lessonId]);

  // Export function
  const exportData = useCallback(async (format: 'csv' | 'json' | 'pdf' = 'json'): Promise<string> => {
    return await trackingService.exportData(lessonId, format);
  }, [lessonId]);

  return {
    // Tracking functions
    trackEvent,
    trackStepComplete,
    trackAssessmentComplete,
    trackResourceInteraction,
    trackQuizResponse,
    trackEngagementCheck,
    trackExitTicket,

    // Data access
    getStudentProgress,
    getAnalytics,
    getLessonEvents,
    getLessonSummary,

    // Export functions
    exportData,

    // State
    isTrackingEnabled,
    currentSessionId
  };
};
